import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ValidationError, ValidationErrorComponent } from '@/components/validation-error/validation-error';
import { EmailSubscriptionTypes } from '@/enums/email-subscription-type';
import { useCreateGuestSubscription, useUpsertSubscription } from '@/hooks/profile/email-subscriptions';
import { useProfile } from '@/hooks/profile/profile';
import { createFormErrorHandler } from '@/utils/form-error-handler';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { TermsPrivacyModal } from '../terms-privacy-modal/terms-privacy-modal';
import emailSubscribeFormStyles from './email-subscribe-form.module.scss';

type EmailSubscribeFormProps = {
  onClose: (success?: boolean) => void;
};

export const EmailSubscribeForm = ({ onClose }: EmailSubscribeFormProps): React.JSX.Element => {
  const { data: profile } = useProfile();
  const { isPending: savingSubscription, mutateAsync: createGuestSubscription } = useCreateGuestSubscription();
  const { isPending: upsertingSubscription, mutateAsync: upsertSubscription } = useUpsertSubscription();
  const saving = savingSubscription || upsertingSubscription;
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const EmailRequired = 'A valid email address is required.';
  const TermsAgreementRequired = 'You must agree to the Terms and Conditions and Privacy Policy by checking the box below.';

  const formSchema = z.object({
    email: z.email({
      message: EmailRequired,
    }),
    termsAgreement: z.literal(true, {
      message: TermsAgreementRequired,
    }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      email: '',
    },
    resolver: zodResolver(formSchema),
  });

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = async (values: z.infer<typeof formSchema>): Promise<void> => {
    setValidationErrors([]);
    try {
      if (profile?.email.toLocaleLowerCase() === values.email.toLocaleLowerCase()) {
        await upsertSubscription({
          emailSubscriptionData: {
            emailSubscriptionTypeId: EmailSubscriptionTypes.DefaultEmailSubscription,
            subscribed: true,
          },
        });
      } else {
        await createGuestSubscription({
          email: values.email,
          emailSubscriptionData: {
            emailSubscriptionTypeId: EmailSubscriptionTypes.DefaultEmailSubscription,
            subscribed: true,
          },
        });
      }

      onClose(true);
    } catch {
      setValidationErrors([{ messages: ['There was an issue trying to subscribe to Cinemark Emails.'] }]);
    }
  };

  const handleFormErrors = createFormErrorHandler<z.infer<typeof formSchema>>(setValidationErrors);

  return (
    <div className="flex flex-col gap-md">
      <div className="flex justify-between items-center gap-md">
        <h2 data-test="email-subscribe-header">Get email updates about movies, rewards and more!</h2>
      </div>

      {!!validationErrors.length &&
        <ValidationErrorComponent errors={validationErrors}/>
      }

      <form noValidate
        className={emailSubscribeFormStyles['email-form']}
        onSubmit={form.handleSubmit(onSubmit, handleFormErrors)}
      >
        <Controller control={form.control}
          name="email"
          render={({ field, fieldState }) =>
            <Field>
              <FieldLabel required
                dataTest="label-email-email-subscribe"
                error={!!fieldState.error}
                htmlFor="input-email-email-subscribe"
              >
                Email Address
              </FieldLabel>
              <Input aria-invalid={fieldState.invalid}
                data-test="input-email-email-subscribe"
                id="input-email-email-subscribe"
                type="email"
                {...field}
              />
            </Field>
          }
        />

        <Controller control={form.control}
          name="termsAgreement"
          render={({ field, fieldState }) =>
            <div className="flex flex-row items-start gap-2">
              <Checkbox aria-invalid={fieldState.invalid}
                checked={field.value}
                className="shrink-0"
                data-test="checkbox-terms"
                id="checkbox-terms"
                onCheckedChange={field.onChange}
              />
              <FieldLabel isCheckbox
                required
                className="inline-block font-semibold text-left leading-4"
                dataTest="label-terms-email-subscribe"
                error={!!fieldState.error}
                htmlFor="checkbox-terms"
              >
                I have read and agree to the
                <TermsPrivacyModal fallbackTitle="Terms and Conditions"
                  policyPageType="termsAndConditions"
                >
                  <button className="mx-1 text-primary font-bold"
                    data-test="button-terms-email-subscribe"
                    type="button"
                  >
                    Terms & Conditions
                  </button>
                </TermsPrivacyModal>
                and
                <TermsPrivacyModal fallbackTitle="Privacy Policy"
                  policyPageType="privacyPolicy"
                >
                  <button className="mx-1 text-primary font-bold"
                    data-test="button-privacy-email-subscribe"
                    type="button"
                  >
                    Privacy Policy<span className="text-[var(--color-secondary)]">.</span>
                  </button>
                </TermsPrivacyModal>
              </FieldLabel>
            </div>
          }
        />

        <Button data-test="button-save-email-subscribe"
          disabled={saving}
          loadingCenter="within"
          showLoading={saving}
          type="submit"
        >
          {saving ? 'Subscribing' : 'Subscribe'}
        </Button>
      </form>
    </div>
  );
};
